import { Component, ReactNode } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import { captureException, withScope } from '@sentry/browser'

import ErrorPage from 'components/ErrorBoundary/ErrorPage'

export interface RemoteModuleErrorBoundaryProps {
    children: ReactNode
    remoteName: string
    // What to render on failure. Defaults to the full-page ErrorPage; app-wide
    // mounts (e.g. the floating chat) pass `null` so a failed remote stays quiet.
    errorFallback?: ReactNode
}

interface State {
    hasError: boolean
    error: Error | null
}

export class RemoteModuleErrorBoundary extends Component<
    RemoteModuleErrorBoundaryProps,
    State
> {
    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error }
    }

    state: State = { hasError: false, error: null }

    componentDidCatch(error: Error): void {
        const { remoteName } = this.props
        datadogRum.addError(error)
        withScope((scope) => {
            scope.setTag('source', 'module_federation')
            scope.setTag('remote', remoteName)
            captureException(error)
        })
    }

    render(): ReactNode {
        const { hasError } = this.state
        const { children, errorFallback } = this.props

        if (hasError) {
            return errorFallback !== undefined ? errorFallback : <ErrorPage />
        }

        return children
    }
}
